import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Button, Card, CardContent, Link, Typography } from '@mui/material';

import { useEventTracking } from '../../../services/statsig';
import { useDisplayBundle } from '../bundleDisplay';
import type { BillingInterval, BundleWithStatus } from '../types';
import { slugifyBundleName } from '../types';

import type { BundlePageLocationState } from './AddOnBundlesPage/AddOnBundlesPage';

interface AddOnCardProps {
  bundle: BundleWithStatus;
  billingInterval: BillingInterval;
  isSelected: boolean;
  onToggle: (bundleId: string) => void;
}

// Helper to detect internal URLs (start with /)
const isInternalUrl = (url: string) => url.startsWith('/');

export const AddOnCard: React.FC<AddOnCardProps> = ({
  bundle: rawBundle,
  billingInterval,
  isSelected,
  onToggle,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { subscription } = useEventTracking();
  const bundle = useDisplayBundle(rawBundle) as BundleWithStatus;

  // Calculate monthly equivalent for annual billing
  const price =
    billingInterval === 'month'
      ? bundle.monthly_unit_amount
      : Math.round(bundle.yearly_unit_amount / 12);
  const formattedPrice = (price / 100).toFixed(0);

  const features = bundle.marketing_features || [];

  // Default to /bundles with tab query param if no learn_more_url is provided
  const learnMoreUrl = bundle.learn_more_url || `/bundles?tab=${slugifyBundleName(bundle.name)}`;

  const handleToggle = () => {
    if (isSelected) {
      // Track remove click (interaction)
      subscription.addonRemoveClicked({
        addon_id: bundle.bundle_id,
        addon_name: bundle.name,
      });

      onToggle(bundle.bundle_id);

      // Track removed (outcome)
      subscription.addonRemoved({
        addon_id: bundle.bundle_id,
        addon_name: bundle.name,
      });
    } else {
      // Track add click (interaction)
      subscription.addonAddClicked({
        addon_id: bundle.bundle_id,
        addon_name: bundle.name,
      });

      onToggle(bundle.bundle_id);

      // Track added (outcome)
      subscription.addonAdded({
        addon_id: bundle.bundle_id,
        addon_name: bundle.name,
        billing_interval: billingInterval,
        monthly_price: bundle.monthly_unit_amount,
        yearly_price: bundle.yearly_unit_amount,
      });
    }
  };

  const handleLearnMoreClick = (e: React.MouseEvent) => {
    subscription.addonLearnMoreClicked({
      addon_id: bundle.bundle_id,
      addon_name: bundle.name,
      learn_more_url: learnMoreUrl,
    });

    // For internal URLs, use React Router navigation with state
    if (isInternalUrl(learnMoreUrl)) {
      e.preventDefault();
      const state: BundlePageLocationState = {
        showAddToPlan: true,
        returnPath: location.pathname + location.search,
      };
      navigate(learnMoreUrl, { state });
    }
    // External URLs will follow the href naturally
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: '2px solid',
        borderColor: 'divider',
        borderRadius: '12px',
        transition: 'all 0.2s ease-in-out',
        backgroundColor: 'background.paper',
        '&:hover': {
          borderColor: 'primary.light',
          boxShadow: 2,
        },
      }}
    >
      <CardContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          p: 3,
        }}
      >
        {/* Header with name and button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2,
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              component="h4"
              sx={{
                fontWeight: 600,
                mb: 0.5,
              }}
            >
              {bundle.name}
            </Typography>
            <Typography
              variant="h5"
              component="div"
              sx={{ fontWeight: 700, color: 'primary.main' }}
            >
              ${formattedPrice}
              <Typography
                component="span"
                variant="body2"
                sx={{ color: 'text.secondary', ml: 0.5 }}
              >
                /mo
              </Typography>
            </Typography>
            {billingInterval === 'year' && (
              <Typography variant="caption" color="text.secondary">
                Billed annually
              </Typography>
            )}
          </Box>

          <Button
            variant={isSelected ? 'contained' : 'outlined'}
            color="primary"
            size="small"
            onClick={handleToggle}
            startIcon={isSelected ? <CheckCircleIcon /> : <AddCircleOutlineIcon />}
            sx={{
              minWidth: '100px',
              fontWeight: 600,
              textTransform: 'none',
            }}
          >
            {isSelected ? 'Added' : 'Add'}
          </Button>
        </Box>

        {/* Description if available */}
        {bundle.description && (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mb: 2,
            }}
          >
            {bundle.description}
          </Typography>
        )}

        {/* Features List */}
        <Box sx={{ flex: 1 }}>
          {features.map((feature, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                mb: 1,
              }}
            >
              <CheckCircleIcon
                sx={{
                  color: isSelected ? 'success.main' : 'text.secondary',
                  fontSize: '1.1rem',
                  mt: 0.25,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  color: 'text.primary',
                }}
              >
                {feature.name}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Learn More Link */}
        <Box sx={{ mt: 2, textAlign: 'right' }}>
          <Link
            href={learnMoreUrl}
            target={isInternalUrl(learnMoreUrl) ? undefined : '_blank'}
            rel={isInternalUrl(learnMoreUrl) ? undefined : 'noopener noreferrer'}
            underline="hover"
            onClick={handleLearnMoreClick}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: '0.875rem',
              color: 'primary.main',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Learn more
            {!isInternalUrl(learnMoreUrl) && <OpenInNewIcon sx={{ fontSize: '0.875rem' }} />}
          </Link>
        </Box>
      </CardContent>
    </Card>
  );
};
